import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import {
  notificationTimeout,
  notificationType,
} from '../common/constants';
import notValid from '../common/notValid';
import { firGetDoorChannels, firUpdateDoorChannel } from '../firebase';

const { Text } = Typography;

// D24-tail (2026-05-15): Door channels are system-managed — channel
// codes (101, 102, ...) correspond to physical hardware and are not
// editable from MA web. Admin can only update the display `label` and
// the `isVisitorEntry` flag. No Add, no Delete. Schema lives at
// sites/{siteId}.information.doorChannel.{code}.{ isVisitorEntry, label }.
export default function SiteConfigDoors({ siteId }) {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const { timeoutError, timeoutSuccess } = notificationTimeout;
  const { typeError, typeSuccess } = notificationType;

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  const fetchChannels = useCallback(
    async (nameSite) => {
      if (notValid(nameSite)) return;
      setLoading(true);
      try {
        const doorMap = await firGetDoorChannels(nameSite);
        const list = Object.entries(doorMap)
          .map(([channelCode, fields]) => ({
            key: channelCode,
            channelCode,
            label: fields?.label || '',
            isVisitorEntry: fields?.isVisitorEntry === true,
          }))
          // Natural numeric sort so 101 < 102 < ... < 1001 (not lex order).
          .sort((a, b) =>
            a.channelCode.localeCompare(b.channelCode, undefined, { numeric: true }),
          );
        setChannels(list);
      } catch (error) {
        console.error('-- Error fetching door channels:', error);
        openMessage('Error fetching door channels', timeoutError, typeError);
      } finally {
        setLoading(false);
      }
    },
    [openMessage, timeoutError, typeError],
  );

  const handleEdit = (record) => {
    setEditingChannel(record);
    form.setFieldsValue({
      label: record.label,
      isVisitorEntry: record.isVisitorEntry,
    });
    setModalOpen(true);
  };

  const handleModalOk = async () => {
    if (!editingChannel) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      await firUpdateDoorChannel(siteId, editingChannel.channelCode, {
        label: values.label.trim(),
        isVisitorEntry: !!values.isVisitorEntry,
      });
      openMessage('Door channel updated', timeoutSuccess, typeSuccess);
      setModalOpen(false);
      form.resetFields();
      setEditingChannel(null);
      fetchChannels(siteId);
    } catch (error) {
      if (error.errorFields) return;
      console.error('-- Error saving door channel:', error);
      openMessage('Error saving door channel', timeoutError, typeError);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchChannels(siteId);
  }, [fetchChannels, siteId]);

  const columns = [
    {
      title: 'Channel',
      dataIndex: 'channelCode',
      width: 110,
      render: (val) => (
        <Text strong style={{ fontFamily: 'monospace' }}>{val}</Text>
      ),
    },
    {
      title: 'Label',
      dataIndex: 'label',
      render: (val) => val || <Text type="secondary">—</Text>,
    },
    {
      title: 'Visitor Entry',
      dataIndex: 'isVisitorEntry',
      width: 140,
      align: 'center',
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) => record.isVisitorEntry === value,
      render: (val) =>
        val ? (
          <Tag color="success">Yes</Tag>
        ) : (
          <Tag color="default">No</Tag>
        ),
    },
    {
      title: 'Actions',
      width: 110,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
            type="link"
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Alert
        message="Door channels are system-managed"
        description={
          <span>
            Channel codes (e.g. <span className="mono">101</span>,{' '}
            <span className="mono">102</span>) match physical hardware
            installed on-site and cannot be added or removed here. You can
            update the display <strong>Label</strong> and toggle{' '}
            <strong>Visitor Entry</strong>.
          </span>
        }
        showIcon
        style={{ marginBottom: 16 }}
        type="info"
      />
      <Table
        columns={columns}
        dataSource={channels}
        loading={loading}
        pagination={false}
        rowKey="key"
      />

      <Modal
        title={
          editingChannel
            ? `Edit Channel ${editingChannel.channelCode}`
            : 'Edit Channel'
        }
        open={modalOpen}
        onOk={handleModalOk}
        confirmLoading={saving}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
          setEditingChannel(null);
        }}
        okText="Save"
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            label="Label"
            name="label"
            rules={[{ required: true, message: 'Please enter a label' }]}
            tooltip="Shown in the resident app and visitor flow."
          >
            <Input placeholder="e.g., Main Gate" maxLength={60} showCount />
          </Form.Item>
          <Form.Item
            label="Visitor Entry"
            name="isVisitorEntry"
            valuePropName="checked"
            tooltip="When on, visitors can trigger this door from the visitor flow."
          >
            <Switch
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

SiteConfigDoors.propTypes = {
  siteId: PropTypes.string.isRequired,
};
